// App root — defines routes with area-based access control.
// Generate routes (/generate/*) require the 'generate' area.
// Manage routes (/manage/*) require the 'manage' area.
// Drafting-wizard access is fully controlled by areas.generate — the
// backend gates the routes via requireArea('generate'); the frontend
// hides nav links for users who don't hold that area.
// Old paths redirect to their /generate/* equivalents.
// /dev/brand is a dev-only brand fixture page; the route is only rendered
// when VITE_DEV_BRAND_FIXTURES=true (never in production builds).

import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router";
import { useAuth } from "./contexts/AuthContext";
import Layout from "./components/common/Layout";
import { isDevBrandFixturesEnabled } from "./utils/featureFlags";
import LoadingSpinner from "./components/common/LoadingSpinner";
import Dashboard from "./pages/Dashboard";
import ContractList from "./pages/ContractList";
import ContractEditor from "./pages/ContractEditor";
import Settings from "./pages/Settings";
import ClauseManager from "./pages/admin/ClauseManager";
import TemplateManager from "./pages/admin/TemplateManager";
import TemplateDetail from "./pages/admin/TemplateDetail";
import OrganisationPage from "./pages/admin/OrganisationPage";
import ContractRegister from "./pages/manage/ContractRegister";
import ManagedContractForm from "./pages/manage/ManagedContractForm";
import ManagedContractDetail from "./pages/manage/ManagedContractDetail";
import SupplierRegister from "./pages/manage/SupplierRegister";
import SupplierDetail from "./pages/manage/SupplierDetail";
import Schedule from "./pages/manage/Schedule";

// Route-level code splitting: the contract-drafting wizard (pulls in the heavy
// PDF reference viewer chain) and the global-admin section (never reached by
// non-admin users) are lazy-loaded so they ship as on-demand chunks rather
// than in the eager entry bundle.
const NewContract = lazy(() => import("./pages/NewContract"));
const Organisations = lazy(() => import("./pages/admin/Organisations"));
const OrganisationDetail = lazy(() => import("./pages/admin/OrganisationDetail"));
const GlobalAdminUsers = lazy(() => import("./pages/admin/GlobalAdminUsers"));
const Statistics = lazy(() => import("./pages/admin/Statistics"));
const AdminContractDetail = lazy(() => import("./pages/admin/AdminContractDetail"));
const BrandFixtures = lazy(() => import("./pages/dev/BrandFixtures"));
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Callback from "./pages/Callback";

interface ProtectedRouteProps {
  children: React.ReactNode;
  area?: string;
}

export function ProtectedRoute({ children, area }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasArea } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (area && !hasArea(area)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function RedirectContractId() {
  const { id } = useParams();
  return <Navigate to={`/generate/contracts/${id}`} replace />;
}

function RedirectTemplateId() {
  const { id } = useParams();
  return <Navigate to={`/generate/templates/${id}`} replace />;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/callback" element={<Callback />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="settings" element={<Settings />} />
        <Route path="organisation" element={<OrganisationPage />} />

        {/* Generate area routes */}
        <Route path="generate/contracts" element={<ContractList />} />
        <Route
          path="generate/contracts/new"
          element={
            <ProtectedRoute area="generate">
              <NewContract />
            </ProtectedRoute>
          }
        />
        <Route
          path="generate/contracts/:id"
          element={
            <ProtectedRoute area="generate">
              <ContractEditor />
            </ProtectedRoute>
          }
        />
        <Route path="generate/templates" element={<TemplateManager />} />
        <Route path="generate/templates/:id" element={<TemplateDetail />} />
        <Route path="generate/clauses" element={<ClauseManager />} />

        {/* Manage area routes — gated on manage permission */}
        <Route
          path="manage/contracts"
          element={
            <ProtectedRoute area="manage">
              <ContractRegister />
            </ProtectedRoute>
          }
        />
        <Route
          path="manage/contracts/new"
          element={
            <ProtectedRoute area="manage">
              <ManagedContractForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="manage/contracts/:id"
          element={
            <ProtectedRoute area="manage">
              <ManagedContractDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="manage/contracts/:id/edit"
          element={
            <ProtectedRoute area="manage">
              <ManagedContractForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="manage/schedule"
          element={
            <ProtectedRoute area="manage">
              <Schedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="manage/suppliers"
          element={
            <ProtectedRoute area="manage">
              <SupplierRegister />
            </ProtectedRoute>
          }
        />
        <Route
          path="manage/suppliers/:id"
          element={
            <ProtectedRoute area="manage">
              <SupplierDetail />
            </ProtectedRoute>
          }
        />

        {/* Global admin routes */}
        <Route path="admin/organisations" element={<Organisations />} />
        <Route
          path="admin/organisations/:orgId/contracts/:contractId"
          element={<AdminContractDetail />}
        />
        <Route
          path="admin/organisations/:id"
          element={<OrganisationDetail />}
        />
        <Route path="admin/users" element={<GlobalAdminUsers />} />
        <Route path="admin/statistics" element={<Statistics />} />

        {/* Redirects from old paths */}
        <Route
          path="contracts/new"
          element={<Navigate to="/generate/contracts/new" replace />}
        />
        <Route path="contracts/:id" element={<RedirectContractId />} />
        <Route
          path="contracts"
          element={<Navigate to="/generate/contracts" replace />}
        />
        <Route path="admin/templates/:id" element={<RedirectTemplateId />} />
        <Route
          path="admin/templates"
          element={<Navigate to="/generate/templates" replace />}
        />
        <Route
          path="admin/clauses"
          element={<Navigate to="/generate/clauses" replace />}
        />

        {/* Dev-only brand fixture page — gated by VITE_DEV_BRAND_FIXTURES flag.
            Redirects to root in production and any env where the flag is off. */}
        <Route
          path="dev/brand"
          element={
            isDevBrandFixturesEnabled() ? (
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center">
                    <LoadingSpinner size="lg" />
                  </div>
                }
              >
                <BrandFixtures />
              </Suspense>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
